import { ref } from 'vue'

export interface Toast {
  id: number
  message: string
  variant?: 'success' | 'error' | 'info' | 'warning'
  duration?: number // ms, default 4000
}

const DEFAULT_DURATION_MS = 4000

let _id = 0
const toasts = ref<Toast[]>([])

export function useToast() {
  function remove(id: number) {
    const i = toasts.value.findIndex((t) => t.id === id)
    if (i !== -1) toasts.value.splice(i, 1)
  }

  function add(toast: Omit<Toast, 'id'>) {
    const id = ++_id
    toasts.value.push({ id, duration: DEFAULT_DURATION_MS, ...toast })
    setTimeout(() => remove(id), toast.duration ?? DEFAULT_DURATION_MS)
    return id
  }

  const success = (message: string, opts?: Partial<Omit<Toast, 'id' | 'message' | 'variant'>>) =>
    add({ ...opts, message, variant: 'success' })

  const error = (message: string, opts?: Partial<Omit<Toast, 'id' | 'message' | 'variant'>>) =>
    add({ ...opts, message, variant: 'error' })

  const info = (message: string, opts?: Partial<Omit<Toast, 'id' | 'message' | 'variant'>>) =>
    add({ ...opts, message, variant: 'info' })

  const warning = (message: string, opts?: Partial<Omit<Toast, 'id' | 'message' | 'variant'>>) =>
    add({ ...opts, message, variant: 'warning' })

  return { toasts, add, remove, success, error, info, warning }
}
